import {
  decrireCritere,
  entiteAdmise,
  pieceRattachement,
  type ReferencePiece,
  type TypePiece,
} from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { api, corps, ecrit, lire } from '../api'
import { LIBELLES_TYPE_PIECE } from '../libelles'
import { ChampIndication } from './Dictee'
import { Formulaire } from './Formulaire'
import { suivreRangement } from './SuiviRangement'

type PieceARattacher = {
  id: string
  entiteId: string | null
  type: TypePiece
  entitesPermises: string[] | null
  indication?: string | null
}

/** Entités où la personne peut rattacher la pièce (fiche de pièce, décision « vérifier »). */
export function entitesPourPiece<E extends { id: string; archivee: boolean; privee?: boolean }>(
  p: Pick<PieceARattacher, 'entitesPermises'>,
  entites: E[],
  moi: { ecriture: readonly string[] } | undefined,
): E[] {
  // Même règle que le serveur (`entiteAdmise`, packages/core) : une pièce venue d'une boîte
  // personnelle va dans les entités de sa propriétaire seulement ; une pièce sans restriction
  // n'entre jamais dans un espace privé.
  const privees = entites.filter((e) => e.privee).map((e) => e.id)
  return entites.filter(
    (e) => !e.archivee && moi && ecrit(moi, e.id) && entiteAdmise(p.entitesPermises, e.id, privees),
  )
}

type Memoire = 'cette_fois' | 'consigne' | 'toujours'

/**
 * Ce que l'humain retient pour les prochaines pièces du fournisseur : rien (cette fois
 * seulement), une consigne (fournisseur à plusieurs endroits : la référence lue mène à
 * l'entité), ou l'entité par défaut (un seul endroit).
 */
function Memorisation({
  pieceId,
  memoire,
  setMemoire,
  criteres,
  setCriteres,
  texte,
  setTexte,
}: {
  pieceId: string
  memoire: Memoire
  setMemoire: (m: Memoire) => void
  criteres: ReferencePiece[] | null
  setCriteres: (c: ReferencePiece[]) => void
  texte: string
  setTexte: (t: string) => void
}) {
  const nom = useId()
  const f = useQuery({
    queryKey: ['pieces', pieceId, 'fournisseur'],
    queryFn: () => lire(api.pieces[':id'].fournisseur.$get({ param: { id: pieceId } })),
  }).data
  if (!f?.fournisseur) return null
  const fournisseur = f.fournisseur.nom
  const choisis = criteres ?? f.references.slice(0, 1)
  const coche = (r: ReferencePiece) =>
    choisis.some((c) => c.type === r.type && c.valeur === r.valeur)
  const multi = f.consignes > 0
  const ailleurs = f.repartition
  // Replié par défaut : l'entité se choisit d'abord ; retenir pour la suite est facultatif.
  return (
    <details className="memorisation" open={memoire !== 'cette_fois'}>
      <summary>Et pour les prochaines pièces de {fournisseur} ? (facultatif)</summary>
      <label className="memorisation-choix">
        <input
          type="radio"
          name={nom}
          checked={memoire === 'cette_fois'}
          onChange={() => setMemoire('cette_fois')}
        />
        Rien à retenir : seulement cette pièce
      </label>
      <label className="memorisation-choix">
        <input
          type="radio"
          name={nom}
          checked={memoire === 'consigne'}
          onChange={() => setMemoire('consigne')}
        />
        Retenir l’entité selon la référence lue sur la pièce (compteur, contrat, adresse) : pour un
        fournisseur qui sert plusieurs entités
      </label>
      {memoire === 'consigne' && (
        <div className="memorisation-consigne">
          {f.references.length === 0 && (
            <p className="discret">
              Aucune référence lue sur la pièce : décrire l’endroit en quelques mots.
            </p>
          )}
          {f.references.map((r) => (
            <label key={`${r.type}:${r.valeur}`} className="memorisation-choix">
              <input
                type="checkbox"
                checked={coche(r)}
                onChange={(e) =>
                  setCriteres(
                    e.target.checked
                      ? [...choisis, r]
                      : choisis.filter((c) => c.type !== r.type || c.valeur !== r.valeur),
                  )
                }
              />
              {decrireCritere(r)}
            </label>
          ))}
          <label className="memorisation-texte">
            Texte libre (facultatif)
            <input
              type="text"
              value={texte}
              maxLength={300}
              placeholder="Résidence principale, abonnement du domicile"
              onChange={(e) => setTexte(e.target.value)}
            />
          </label>
          <p className="aide">
            Les critères se comparent sans IA ; le texte n’est lu par Claude que si aucun critère ne
            tranche. Une pièce d’un endroit non couvert restera à vérifier.
          </p>
        </div>
      )}
      <label className="memorisation-choix">
        <input
          type="radio"
          name={nom}
          disabled={multi}
          checked={memoire === 'toujours'}
          onChange={() => setMemoire('toujours')}
        />
        Toujours cette entité pour toutes les pièces de {fournisseur}
      </label>
      {multi && (
        <p className="aide">
          {fournisseur} a déjà des entités retenues selon la référence : « toujours » ne servirait
          pas.
        </p>
      )}
      {ailleurs.length > 0 && (
        <p className="alerte">
          {fournisseur} a déjà des pièces rattachées à :{' '}
          {ailleurs.map((a) => `${a.entite} (${a.pieces})`).join(', ')}. S’il dessert plusieurs
          entités, préférer « selon la référence lue » à « toujours ».
        </p>
      )}
    </details>
  )
}

/**
 * Rattacher une pièce à une entité : ferme sa décision « vérifier ». Le type est celui de la
 * lecture (il se corrige sur la fiche) ; une indication écrite ou dictée aide Claude à la placer.
 */
export function RattacherPiece({
  p,
  ecrivables,
  libelleBouton = 'Rattacher',
  onFait,
}: {
  p: PieceARattacher
  ecrivables: { id: string; nom: string }[]
  libelleBouton?: string
  onFait?: () => void | Promise<void>
}) {
  const client = useQueryClient()
  const idIndication = useId()
  const [indication, setIndication] = useState('')
  const [memoire, setMemoire] = useState<Memoire>('cette_fois')
  const [criteres, setCriteres] = useState<ReferencePiece[] | null>(null)
  const [texteConsigne, setTexteConsigne] = useState('')
  const references = useQuery({
    queryKey: ['pieces', p.id, 'fournisseur'],
    queryFn: () => lire(api.pieces[':id'].fournisseur.$get({ param: { id: p.id } })),
  }).data?.references
  return (
    <div className="pile">
      <p className="discret">
        Lue comme : <strong>{LIBELLES_TYPE_PIECE[p.type]}</strong> (se corrige sur la fiche de la
        pièce).
        {p.indication && <> Indication déjà donnée : « {p.indication} ».</>}
      </p>
      <ChampIndication id={idIndication} valeur={indication} onChange={setIndication} />
      <Memorisation
        pieceId={p.id}
        memoire={memoire}
        setMemoire={setMemoire}
        criteres={criteres}
        setCriteres={setCriteres}
        texte={texteConsigne}
        setTexte={setTexteConsigne}
      />
      <Formulaire
        champs={[
          {
            nom: 'entiteId',
            libelle: 'Entité',
            type: 'choix',
            requis: true,
            options: ecrivables.map((e) => ({ valeur: e.id, libelle: e.nom })),
          },
        ]}
        initial={{ entiteId: p.entiteId ?? '' }}
        libelleBouton={libelleBouton}
        onValider={async (v) => {
          const texte = indication.trim().slice(0, 500)
          const consigne =
            memoire === 'consigne'
              ? {
                  criteres: criteres ?? (references ?? []).slice(0, 1),
                  texte: texteConsigne.trim() || null,
                }
              : undefined
          await lire(
            api.pieces[':id'].rattacher.$post({
              param: { id: p.id },
              json: corps(pieceRattachement, {
                ...v,
                ...(texte && { indication: texte }),
                ...(memoire === 'toujours' && { retenirFournisseur: true }),
                ...(consigne && { consigneFournisseur: consigne }),
              }),
            }),
          )
          setIndication('')
          await client.invalidateQueries()
          suivreRangement(p.id)
          await onFait?.()
        }}
      />
    </div>
  )
}
